import React, { useState, useEffect } from "react";
import { ControlTradingDaysService } from "../../services/controlTradingDaysService";
import styles from "./ControlTradingDays.module.css";
import { usePermission } from "../../hooks/usePermissions";
import { formatAdminDate } from "../../utils/datetime";

const EditIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.12 2.12 0 1 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
);

const TrashIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        <path d="M10 11v6" />
        <path d="M14 11v6" />
        <path d="M9 6V4h6v2" />
    </svg>
);

const ControlTradingDays = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [page, setPage] = useState(1);
    const LIMIT = 10;

    const getTodayDate = () => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };

    const {hasPermission} = usePermission()

    const DEFAULT_TIMES = {
        premarketopentime: "09:00:00",
        marketstarttime: "09:15:00",
        marketendtime: "15:30:00",
        marketclosetime: "16:30:00",
    };

    const [formData, setFormData] = useState({
        date: getTodayDate(),
        ...DEFAULT_TIMES
    });


    const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'DESC' });

    useEffect(() => {
        fetchData();
    }, [sortConfig]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const res = await ControlTradingDaysService.getAll({
                sortBy: sortConfig.key,
                order: sortConfig.direction
            });
            if (res) {
                setData(res);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (key) => {
        let direction = 'ASC';
        if (sortConfig.key === key && sortConfig.direction === 'ASC') {
            direction = 'DESC';
        }
        setSortConfig({ key, direction });
        setPage(1);
    };

    const getSortIndicator = (key) => {
        if (sortConfig.key !== key) {
            return (
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.sortIconInactive}>
                    <path d="M7 15l5 5 5-5" />
                    <path d="M7 9l5-5 5 5" />
                </svg>
            );
        }
        return sortConfig.direction === 'ASC' ? (
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.sortIconActive}>
                <path d="M12 19V5" />
                <path d="M5 12l7-7 7 7" />
            </svg>
        ) : (
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.sortIconActive}>
                <path d="M12 5v14" />
                <path d="M19 12l-7 7-7-7" />
            </svg>
        );
    };

    const totalCount = data.length;
    const totalPages = Math.ceil(totalCount / LIMIT);
    const indexOfLastItem = page * LIMIT;
    const indexOfFirstItem = indexOfLastItem - LIMIT;
    const currentItems = data.slice(indexOfFirstItem, indexOfLastItem);

    const handleNext = () => {
        if (page < totalPages) setPage(page + 1);
    };

    const handlePrevious = () => {
        if (page > 1) setPage(page - 1);
    };

    // ... (rest of the file until table header)

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingItem) {
                await ControlTradingDaysService.update(editingItem.id, formData);
                alert("Updated successfully");
            } else {
                await ControlTradingDaysService.create(formData);
                alert("Created successfully");
            }
            setIsModalOpen(false);
            setEditingItem(null);
            setFormData({
                date: getTodayDate(),
                ...DEFAULT_TIMES
            });
            fetchData();
        } catch (error) {
            console.error("Error saving data:", error);
            const message = error.response?.data?.message || "Failed to save data";
            alert(message);
        }
    };

    const handleEdit = (item) => {
        setEditingItem(item);
        // Format date/time to fit inputs if necessary. 
        // Assuming backend returns standard formats or we adjust here.
        // For date input: yyyy-MM-dd
        // For time input: HH:mm

        // Helper to extract clean date/time strings
        const formatDate = (d) => {
            if (!d) return "";
            const date = new Date(d);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };
        const formatTime = (t) => {
            if (!t) return "";
            // If it's a timestamp string like "1970-01-01T09:15:00.000Z", extract time
            // Or if it's "09:15:00", keep as is
            if (t.includes('T')) return new Date(t).toISOString().substring(11, 16);
            return t.substring(0, 5);
        };

        setFormData({
            date: formatDate(item.date),
            premarketopentime: formatTime(`${item.premarketopentime}`),
            marketstarttime: formatTime(item.marketstarttime),
            marketendtime: formatTime(item.marketendtime),
            marketclosetime: formatTime(item.marketclosetime),
        });
        setIsModalOpen(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this trading day?")) {
            try {
                await ControlTradingDaysService.delete(id);
                fetchData();
            } catch (error) {
                console.error("Error deleting item:", error);
            }
        }
    };

    const openAddModal = () => {
        setEditingItem(null);
        setFormData({
            date: getTodayDate(),
            ...DEFAULT_TIMES
        });
        setIsModalOpen(true);
    };

    return (
        <div className={styles.container}>
            {/* Header */}
            <div className={styles.pageHeader}>
                <div className={styles.headerLeft}>
                    <h1 className={styles.pageTitle}>Control Trading Days</h1>
                    <p className={styles.pageSubtitle}>Manage market session timings and trading schedules</p>
                </div>
                <div className={styles.headerRight}>
                    {hasPermission("CREATE_CONTROLTRADING") && (
                        <button className={styles.addButton} onClick={openAddModal}>
                            + Add New
                        </button>
                    )}
                </div>
            </div>

            {/* Table */}
            <div className={styles.tableWrap}>
                {loading ? (
                    <p className={styles.loadingText}>Loading...</p>
                ) : (
                    <>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th onClick={() => handleSort('date')} className={styles.cursorPointer}>
                                        Date {getSortIndicator('date')}
                                    </th>
                                    <th onClick={() => handleSort('premarketopentime')} className={styles.cursorPointer}>
                                        Pre-Market Open {getSortIndicator('premarketopentime')}
                                    </th>
                                    <th onClick={() => handleSort('marketstarttime')} className={styles.cursorPointer}>
                                        Market Start {getSortIndicator('marketstarttime')}
                                    </th>
                                    <th onClick={() => handleSort('marketendtime')} className={styles.cursorPointer}>
                                        Market End {getSortIndicator('marketendtime')}
                                    </th>
                                    <th onClick={() => handleSort('marketclosetime')} className={styles.cursorPointer}>
                                        Market Close {getSortIndicator('marketclosetime')}
                                    </th>
                                    <th className={styles.textCenter}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentItems.map((item) => (
                                    <tr key={item.id}>
                                        <td>{formatAdminDate(item.date)}</td>
                                        <td>{item.premarketopentime}</td>
                                        <td>{item.marketstarttime}</td>
                                        <td>{item.marketendtime}</td>
                                        <td>{item.marketclosetime}</td>
                                        <td>
                                            <div className={styles.actionButtons}>
                                                {hasPermission("MODIFY_CONTROLTRADING") && (
                                                    <button onClick={() => handleEdit(item)} className={styles.editBtn} title="Edit">
                                                        <EditIcon />
                                                    </button>
                                                )}
                                                {hasPermission("DELETE_CONTROLTRADING") && (
                                                    <button onClick={() => handleDelete(item.id)} className={styles.deleteBtn} title="Delete">
                                                        <TrashIcon />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Pagination footer */}
                        <div className={styles.imTableFooter}>
                            <span>Showing {currentItems.length} records (Total: {totalCount})</span>
            
                            {totalPages > 1 && (
                                <div className={styles.imPagination}>
                                    <button
                                        type="button"
                                        className={styles.imPageBtn}
                                        disabled={page === 1}
                                        onClick={handlePrevious}
                                    >
                                        {"<"}
                                    </button>
                                    
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => {
                                        if (
                                            p === 1 ||
                                            p === totalPages ||
                                            (p >= page - 1 && p <= page + 1)
                                        ) {
                                            return (
                                                <button
                                                    type="button"
                                                    key={p}
                                                    className={`${styles.imPageBtn} ${page === p ? styles.active : ""}`}
                                                    onClick={() => setPage(p)}
                                                >
                                                    {p}
                                                </button>
                                            );
                                        }
            
                                        if (p === page - 2 || p === page + 2) {
                                            return (
                                                <span key={p} className={styles.paginationDots}>
                                                    ...
                                                </span>
                                            );
                                        }
            
                                        return null;
                                    })}
            
                                    <button
                                        type="button"
                                        className={styles.imPageBtn}
                                        disabled={page >= totalPages}
                                        onClick={handleNext}
                                    >
                                        {">"}
                                    </button>
                                </div>
                            )}
                            
                            <span className={styles.pageCountInfo}>
                                Page {page} of {totalPages || 1}
                            </span>
                        </div>
                    </>
                )}
            </div>

            {isModalOpen && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modal}>
                        <h3>{editingItem ? "Edit Trading Day" : "Add Trading Day"}</h3>
                        <form onSubmit={handleSubmit}>
                            <div className={styles.formGroup}>
                                <label>Date</label>
                                <input
                                    type="date"
                                    name="date"
                                    value={formData.date}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                            <div className={styles.formGroup}>
                                <label>Pre-Market Open Time</label>
                                <input
                                    type="time" // step="1" if seconds needed
                                    name="premarketopentime"
                                    value={formData.premarketopentime}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                            <div className={styles.formGroup}>
                                <label>Market Start Time</label>
                                <input
                                    type="time"
                                    name="marketstarttime"
                                    value={formData.marketstarttime}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                            <div className={styles.formGroup}>
                                <label>Market End Time</label>
                                <input
                                    type="time"
                                    name="marketendtime"
                                    value={formData.marketendtime}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                            <div className={styles.formGroup}>
                                <label>Market Close Time</label>
                                <input
                                    type="time"
                                    name="marketclosetime"
                                    value={formData.marketclosetime}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                            <div className={styles.modalActions}>
                                <button type="button" onClick={() => setIsModalOpen(false)} className={styles.cancelButton}>Cancel</button>
                                <button type="submit" className={styles.saveButton}>{editingItem ? "Update" : "Save"}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ControlTradingDays;
